import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import config from '../../config';
import { trackButtonClick } from '../../hooks/useTelemetry';
import { brandLogo, useSiteContent } from '../../lib/siteContent';

export default function Footer() {
  const site = useSiteContent();
  const footer = site.footer || {};
  const logoSrc = brandLogo(site);
  return (
    <>
      {/* Founder's Signature */}
      <section data-testid="founder-signature" style={{ backgroundColor: '#F9F9F9' }}>
        <div className="max-w-[600px] mx-auto px-6 py-16 text-center">
          <div className="w-10 h-px mx-auto mb-8" style={{ backgroundColor: '#C5A054' }} />
          <p className="text-base leading-relaxed italic" style={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#4A4A4A' }}>
            "{footer.founder_quote || "As a professional in Gurgaon, I built BookMyJab because my own family deserved better than crowded hospitals. We treat your family like our own."}"
          </p>
          <p className="mt-6 text-xs font-medium" style={{ fontFamily: 'Montserrat, sans-serif', color: '#4A4A4A', letterSpacing: '2px', textTransform: 'uppercase' }}>
            {footer.founder_credit || "— Founder, BookMyJab"}
          </p>
        </div>
      </section>

      <footer data-testid="public-footer" className="bg-[#311b52] text-white pb-24 md:pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand — 40% larger logo */}
          <div className="md:col-span-1">
            <img src={logoSrc} alt="BookMyJab" style={{ width: '180px', height: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} className="mb-3" />
            <p
              data-testid="footer-tagline-signature"
              className="text-[11px] italic font-medium text-[#f37721] tracking-[0.14em] uppercase mb-4"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {footer.tagline || "Uncompromising Care. Seamlessly Yours."}
            </p>
            <p className="text-white/60 text-sm leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {footer.blurb || "Premium at-home vaccination for discerning families. Strict hygiene protocols, digital records, and concierge-level care at your doorstep."}
            </p>
            <p
              data-testid="footer-pillars-monogram"
              className="mt-5 text-[10px] uppercase tracking-[0.2em] text-white/40 leading-relaxed"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
            {footer.pillars || "Qualified Professionals · Clinical Reliability · Intelligent Technology · Trusted Experience"}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-sm mb-5 tracking-wider uppercase text-white/80" style={{ fontFamily: 'Poppins, sans-serif' }}>Quick Links</h4>
            <ul className="space-y-3">
              {[
                { label: 'The Journey', href: '#how-it-works' },
                { label: 'Services', href: '#services' },
                { label: 'Why BookMyJab', href: '#why-us' },
                { label: 'FAQ', href: '#faq' },
              ].map(link => (
                <li key={link.label}>
                  <a href={link.href} className="text-white/50 hover:text-[#f37721] text-sm transition-colors" style={{ fontFamily: 'Montserrat' }}>
                    {link.label}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/about" data-testid="footer-about-link"
                  className="text-white/50 hover:text-[#f37721] text-sm transition-colors" style={{ fontFamily: 'Montserrat' }}>
                  About BookMyJab
                </Link>
              </li>
              <li>
                <Link to="/vaccination-catalogue" data-testid="footer-catalogue-link"
                  className="text-white/50 hover:text-[#f37721] text-sm transition-colors" style={{ fontFamily: 'Montserrat' }}>
                  Vaccination Catalogue
                </Link>
              </li>
              <li>
                <Link to="/vaccination-catalogue" data-testid="footer-request-link"
                  className="text-white/50 hover:text-[#f37721] text-sm transition-colors" style={{ fontFamily: 'Montserrat' }}>
                  Book now
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-sm mb-5 tracking-wider uppercase text-white/80" style={{ fontFamily: 'Poppins, sans-serif' }}>Services</h4>
            <ul className="space-y-3">
              {['Adult Vaccinations', 'Paediatric Vaccinations', 'Elderly Vaccinations', 'Travel Vaccinations'].map(s => (
                <li key={s}>
                  <span className="text-white/50 text-sm" style={{ fontFamily: 'Montserrat' }}>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact — phone removed, redistributed */}
          <div>
            <h4 className="font-semibold text-sm mb-5 tracking-wider uppercase text-white/80" style={{ fontFamily: 'Poppins, sans-serif' }}>Contact</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#f37721] mt-0.5 shrink-0" />
                <div>
                  <a href={`mailto:${footer.email || config.SUPPORT_EMAIL}`} className="text-white/60 hover:text-white text-sm block" style={{ fontFamily: 'Montserrat' }}>
                    {footer.email || config.SUPPORT_EMAIL}
                  </a>
                  <span className="text-white/30 text-xs" style={{ fontFamily: 'Montserrat' }}>We respond within 15 minutes</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#f37721] mt-0.5 shrink-0" />
                <a href={`tel:${String(footer.phone || '').replace(/\s/g, '') || '+919873554343'}`} data-testid="footer-phone"
                  className="bmj-tel-mobile-only text-white text-sm block whitespace-nowrap" style={{ fontFamily: 'Montserrat' }}>
                  {footer.phone || "+91 9873 55 4343"}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#f37721] mt-0.5 shrink-0" />
                <div>
                  <span className="text-white/60 text-sm block" style={{ fontFamily: 'Montserrat' }}>
                    {footer.address_line1 || "Golf Course Road & Extension,"}
                  </span>
                  <span className="text-white/60 text-sm block" style={{ fontFamily: 'Montserrat' }}>
                    {footer.address_line2 || "DLF Phases, Sector 54-57"}
                  </span>
                  <span className="text-white/40 text-xs block mt-1" style={{ fontFamily: 'Montserrat' }}>
                    {footer.address_city || "Gurgaon, Haryana, India"}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom — corporate attribution */}
        <div className="mt-14 pt-8 border-t border-white/8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-xs" style={{ fontFamily: 'Montserrat' }}>
            {footer.copyright || "© BookMyJab Pvt. Ltd. All rights reserved."}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center sm:justify-end">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-white/40 hover:text-[#f37721] text-xs transition-colors" style={{ fontFamily: 'Montserrat' }}>Privacy Policy</Link>
            <Link to="/terms-of-service" data-testid="footer-terms-link" className="text-white/40 hover:text-[#f37721] text-xs transition-colors" style={{ fontFamily: 'Montserrat' }}>Terms of Service</Link>
            <Link to="/refund-policy" data-testid="footer-refund-link" className="text-white/40 hover:text-[#f37721] text-xs transition-colors" style={{ fontFamily: 'Montserrat' }}>Refund Policy</Link>
            <Link to="/medical-vaccination-consent" data-testid="footer-medical-link" className="text-white/40 hover:text-[#f37721] text-xs transition-colors" style={{ fontFamily: 'Montserrat' }}>Medical & Vaccination Consent</Link>
            <Link to="/grievance" data-testid="footer-grievance-link" className="text-white/40 hover:text-[#f37721] text-xs transition-colors" style={{ fontFamily: 'Montserrat' }}>Grievance & Data Rights</Link>
            <a href="#contact" className="text-white/40 hover:text-[#f37721] text-xs transition-colors" style={{ fontFamily: 'Montserrat' }}>Support</a>
          </div>
        </div>

        {/* Follow Our Care — Social Links */}
        <div className="mt-8 pt-6 border-t border-white/8">
          <div className="flex flex-col items-center sm:items-end gap-3">
            <p className="text-white/30 text-[10px] uppercase tracking-[0.15em]" style={{ fontFamily: 'Montserrat' }}>Follow Our Care</p>
            <div className="flex items-center gap-6" data-testid="social-links">
              {[
                { href: footer.instagram || 'https://www.instagram.com/bookmyjab', label: 'Instagram', path: 'M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2m-.2 2A3.6 3.6 0 004 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5M12 7a5 5 0 110 10 5 5 0 010-10m0 2a3 3 0 100 6 3 3 0 000-6z' },
                { href: footer.linkedin || 'https://linkedin.com/company/bookmyjab', label: 'LinkedIn', path: 'M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14m-.5 15.5v-5.3a3.26 3.26 0 00-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 011.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 001.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 00-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z' },
                { href: footer.twitter || 'https://twitter.com/bookmyjab', label: 'X', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
                { href: footer.facebook || 'https://www.facebook.com/share/1FCaED6NHv/', label: 'Facebook', path: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z' },
              ].filter((s) => s.href).map(({ href, label, path }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  data-testid={`social-${label.toLowerCase()}`}
                  onClick={() => trackButtonClick(`Social: ${label}`)}
                  className="flex items-center justify-center w-11 h-11 rounded-full hover:scale-110 hover:brightness-125 transition-all duration-200"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C5A059" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d={path} />
                  </svg>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
}
